import axios from './authService';
import { API_ENDPOINTS } from '../config/apiConfig';
import store from '../redux/store';
import { getDeviceIdForAppiumScripts } from '../utilities/getDevideIdForAppiumScripts';
import { SUMMARY_LIMIT } from '../components/ActiveRuns/summary/utils/runSummaryUtils';

export async function fetchActiveRuns(deviceId = null) {
  const params = deviceId ? `?device_id=${deviceId}` : '';
  const res = await axios.get(`${API_ENDPOINTS.GET_ACTIVE_RUNS}${params}`);
  return res.data;
}

export async function fetchRunHistory(limit = 100, deviceId = null) {
  let params = `?limit=${limit}`;
  if (deviceId) params += `&device_id=${deviceId}`;
  const res = await axios.get(`${API_ENDPOINTS.GET_RUN_HISTORY}${params}`);
  return res.data;
}

function buildRunSummaryParams(
  limit = SUMMARY_LIMIT,
  deviceId = null,
  start_date = null,
  end_date = null,
  android_build = null,
  ios_build = null,
) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (deviceId) params.set('device_id', deviceId);
  if (start_date) params.set('start_date', start_date);
  if (end_date) params.set('end_date', end_date);
  if (android_build) params.set('android_build', android_build);
  if (ios_build) params.set('ios_build', ios_build);
  return params;
}

export async function fetchRunSummaryDashboard(
  limit = SUMMARY_LIMIT,
  deviceId = null,
  start_date = null,
  end_date = null,
  android_build = null,
  ios_build = null,
) {
  const params = buildRunSummaryParams(
    limit,
    deviceId,
    start_date,
    end_date,
    android_build,
    ios_build,
  );

  const res = await axios.get(`${API_ENDPOINTS.GET_RUN_SUMMARY}?${params}`);
  return res.data;
}

export async function fetchFailedRuns(
  limit = SUMMARY_LIMIT,
  deviceId = null,
  start_date = null,
  end_date = null,
  android_build = null,
  ios_build = null,
) {
  const params = buildRunSummaryParams(
    limit,
    deviceId,
    start_date,
    end_date,
    android_build,
    ios_build,
  );

  try {
    const response = await axios.get(`${API_ENDPOINTS.GET_FAILED_RUNS}?${params}`);
    return {
      runs: response.data.runs,
      reportData: response.data.reportData,
    };
  } catch (error) {
    if (error.response?.status !== 404) {
      throw error;
    }
  }
}

export async function fetchBuildVersions(limit = 500) {
  const params = new URLSearchParams({ limit: String(limit) });
  const res = await axios.get(`${API_ENDPOINTS.GET_RUNS_BUILD_VERSIONS}?${params}`);
  return res.data;
}

export async function fetchRunSummaryExport({ deviceId, startDate, endDate, appVersion }) {
  const params = new URLSearchParams();
  if (deviceId) params.set('device_id', deviceId);
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (appVersion) params.set('app_version', appVersion);
  const res = await axios.get(`${API_ENDPOINTS.RUN_SUMMARY_EXPORT}?${params}`);

  return res.data;
}

export async function generateRunSummaryAgentReport(params) {
  const payload = {
    limit: params.limit ?? SUMMARY_LIMIT,
  };

  if (params?.deviceId) payload.device_id = params.deviceId;
  if (params?.startDate) payload.start_date = params.startDate;
  if (params?.endDate) payload.end_date = params.endDate;
  if (params?.androidBuildVersion) payload.android_build = params.androidBuildVersion;
  if (params?.iosBuildVersion) payload.ios_build = params.iosBuildVersion;

  try {
    const res = await axios.post(API_ENDPOINTS.RUN_SUMMARY_AGENT, payload);
    return res.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to generate run summary report.',
    );
  }
}

export async function fetchQueueStatus() {
  const res = await axios.get(API_ENDPOINTS.QUEUE_STATUS);
  return res.data;
}

export async function fetchRunLogs(runId, fromIndex = 0) {
  const res = await axios.get(`${API_ENDPOINTS.GET_RUN_LOGS(runId)}?from_index=${fromIndex}`);
  return res.data;
}

export async function cancelRun(runId) {
  const res = await axios.post(API_ENDPOINTS.CANCEL_RUN(runId));
  return res.data;
}

export async function cancelAllRuns() {
  const state = store.getState();
  const selectedDevice = state.platform.selectedDevice;

  const res = await axios.post(API_ENDPOINTS.CANCEL_ALL_RUNS, {
    device_id: getDeviceIdForAppiumScripts(selectedDevice),
  });

  return res.data;
}

export async function analyzeBug(payload) {
  const res = await axios.post(API_ENDPOINTS.ANALYZE_BUG, payload);

  return res.data;
}

export async function createJiraTicket(payload) {
  const res = await axios.post(API_ENDPOINTS.CREATE_JIRA_TICKET, payload);

  return res.data;
}
